import { useEffect, useState } from 'react';
import { isParticipantResponseSubmitted } from '../../../lib/liveSessionDisplay';
import { resolveParticipantTotal } from '../../../lib/sessionParticipantRoster';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { isValueSession } from '../utils/sessionHelpers';
import { getValueSessionGapStats } from '../utils/valueSessionGaps';

export interface SubmissionStats {
  submittedCount: number;
  totalParticipants: number;
  mySubmitted: boolean;
  myInProgress: boolean;
  loading: boolean;
  /** GAP-5: items still needing values (value sessions only) */
  itemsWithGaps?: number;
  totalItems?: number;
  itemsComplete?: number;
}

const EMPTY: SubmissionStats = {
  submittedCount: 0,
  totalParticipants: 0,
  mySubmitted: false,
  myInProgress: false,
  loading: true,
};

export function useMySubmissionStatus(
  session: PrioritizationSession | null | undefined,
  userId: string,
): SubmissionStats {
  const [stats, setStats] = useState<SubmissionStats>(EMPTY);

  useEffect(() => {
    if (!session?.id || !userId) {
      setStats({ ...EMPTY, loading: false });
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      setStats({ ...EMPTY, loading: false, totalParticipants: session.participantIds?.length ?? 0 });
      return;
    }

    let cancelled = false;

    async function load() {
      const totalParticipants = Math.max(session!.participantIds?.length ?? 0, 1);

      const { data: toolSessions } = await supabase!
        .from('tool_sessions')
        .select('id, participant_ids')
        .eq('session_id', session!.id)
        .in('phase', ['collecting', 'revealed', 'finalized'])
        .order('created_at', { ascending: false })
        .limit(1);

      const toolSession = toolSessions?.[0] as { id: string; participant_ids?: string[] } | undefined;
      if (!toolSession) {
        if (!cancelled) {
          setStats({
            submittedCount: 0,
            totalParticipants,
            mySubmitted: false,
            myInProgress: false,
            loading: false,
          });
        }
        return;
      }

      const participantIds = toolSession.participant_ids ?? session!.participantIds ?? [];
      const { data: responses } = await supabase!
        .from('tool_session_responses')
        .select('participant_id, is_complete, results')
        .eq('tool_session_id', toolSession.id);

      const submitted = (responses ?? []).filter(r =>
        isParticipantResponseSubmitted(r as { is_complete?: boolean; results?: unknown[] }),
      );
      const responderIds = submitted.map(
        (r) => (r as { participant_id: string }).participant_id,
      );

      const myRow = (responses ?? []).find(r => (r as { participant_id: string }).participant_id === userId);
      const mySubmitted = myRow
        ? isParticipantResponseSubmitted(myRow as { is_complete?: boolean; results?: unknown[] })
        : false;
      const myInProgress = !!myRow && !mySubmitted;

      if (!cancelled) {
        setStats({
          submittedCount: submitted.length,
          totalParticipants: resolveParticipantTotal(
            [...participantIds, ...(session!.participantIds ?? [])],
            responderIds,
          ),
          mySubmitted,
          myInProgress,
          loading: false,
        });
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [session, userId]);

  return stats;
}

/** Batch stats for dashboard cards keyed by session id */
export function useBatchSubmissionStats(
  sessions: PrioritizationSession[],
  userId: string,
): Record<string, SubmissionStats> {
  const [map, setMap] = useState<Record<string, SubmissionStats>>({});
  const exceptions = useValueProposalStore((s) => s.exceptions);

  useEffect(() => {
    if (!userId || sessions.length === 0) {
      // Keep the empty map reference-stable: callers pass a fresh `sessions`
      // array each render, so an unconditional setMap({}) here re-renders the
      // caller, re-runs this effect, and loops forever on an empty dashboard.
      setMap(prev => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    let cancelled = false;
    const rankingSessions = sessions.filter((s) => !isValueSession(s));
    const valueSessions = sessions.filter(isValueSession);
    const sessionIds = rankingSessions.map((s) => s.id);

    async function load() {
      const next: Record<string, SubmissionStats> = {};

      // GAP-5: value sessions use eligibility + exceptions, not tool_session_responses
      for (const s of valueSessions) {
        const gaps = getValueSessionGapStats(s, exceptions);
        const total = Math.max(s.participantIds?.length ?? 0, 1);
        next[s.id] = {
          submittedCount: gaps.myComplete ? 1 : 0,
          totalParticipants: total,
          mySubmitted: gaps.myComplete,
          myInProgress: gaps.myInProgress,
          loading: false,
          itemsWithGaps: gaps.itemsWithGaps,
          totalItems: gaps.totalItems,
          itemsComplete: gaps.itemsComplete,
        };
      }

      if (sessionIds.length === 0) {
        if (!cancelled) setMap(next);
        return;
      }

      const { data: toolSessions } = await supabase!
        .from('tool_sessions')
        .select('id, session_id, participant_ids')
        .in('session_id', sessionIds)
        .in('phase', ['collecting', 'revealed', 'finalized']);

      const tsBySession = new Map<string, { id: string; participant_ids?: string[] }>();
      for (const ts of toolSessions ?? []) {
        const row = ts as { id: string; session_id: string; participant_ids?: string[] };
        if (!tsBySession.has(row.session_id)) tsBySession.set(row.session_id, row);
      }

      const toolSessionIds = [...tsBySession.values()].map(t => t.id);
      const responsesByTs: Record<string, Array<{ participant_id: string; is_complete?: boolean; results?: unknown[] }>> = {};

      if (toolSessionIds.length > 0) {
        const { data: responses } = await supabase!
          .from('tool_session_responses')
          .select('tool_session_id, participant_id, is_complete, results')
          .in('tool_session_id', toolSessionIds);

        for (const r of responses ?? []) {
          const row = r as { tool_session_id: string; participant_id: string; is_complete?: boolean; results?: unknown[] };
          if (!responsesByTs[row.tool_session_id]) responsesByTs[row.tool_session_id] = [];
          responsesByTs[row.tool_session_id].push(row);
        }
      }

      for (const s of rankingSessions) {
        const ts = tsBySession.get(s.id);
        const responses = ts ? (responsesByTs[ts.id] ?? []) : [];
        const submitted = responses.filter(r => isParticipantResponseSubmitted(r));
        const submittedCount = submitted.length;
        const myRow = responses.find(r => r.participant_id === userId);
        next[s.id] = {
          submittedCount,
          totalParticipants: resolveParticipantTotal(
            [...(ts?.participant_ids ?? []), ...(s.participantIds ?? [])],
            submitted.map((r) => r.participant_id),
          ),
          mySubmitted: myRow ? isParticipantResponseSubmitted(myRow) : false,
          myInProgress: !!myRow && !isParticipantResponseSubmitted(myRow),
          loading: false,
        };
      }

      if (!cancelled) setMap(next);
    }

    void load();
    return () => { cancelled = true; };
  }, [sessions, userId, exceptions]);

  return map;
}
